import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { toast } from "sonner";
import ContractDrawer from "@/components/ContractDrawer";
import PartnerizeImportDialog from "@/components/PartnerizeImportDialog";
import SkimlinksImportDialog from "@/components/SkimlinksImportDialog";
import { NetworkHubPanel } from "./NetworkHubPanel";
import ManualBrandDrawer, { BrandAuditDrawer } from "./brand_manual/ManualBrandDrawer";

import NetworkActionsBar from "./brands/NetworkActionsBar";
import BrandsFilterBar from "./brands/BrandsFilterBar";
import EmptySearchPrompt from "./brands/EmptySearchPrompt";
import BrandsTable from "./brands/BrandsTable";
import BrandsPagination from "./brands/BrandsPagination";
import MrgeImportDialog from "./brands/MrgeImportDialog";
import TrafficRulesDrawer from "./brands/TrafficRulesDrawer";
import BrandRefPolicyDrawer from "./brands/BrandRefPolicyDrawer";
import { useNetworkActions } from "./brands/useNetworkActions";

/**
 * Admin Catalog / Brand Network Hub.
 *
 * This page is the orchestrator — it owns the brand list state, the
 * search/filter state, and all the drawers / dialogs. The visual
 * sub-components live in `./brands/*`:
 *
 *   • NetworkHubPanel     — per-network status cards (lives one dir up).
 *   • NetworkActionsBar   — bulk-import & sync buttons gated by filter.
 *   • BrandsFilterBar     — search, network chips, Excel export.
 *   • EmptySearchPrompt   — amber "no matches → check upstream" prompt.
 *   • BrandsTable         — table rows with inline commission + geo
 *                           editors + action cluster.
 *   • BrandsPagination    — server-paginated page chooser.
 *   • MrgeImportDialog    — self-contained mrge import modal.
 *
 * All third-party drawers (ContractDrawer / Partnerize /Skimlinks
 * import / ManualBrandDrawer / BrandAuditDrawer) are still mounted
 * from this file because their open-state is tied to row clicks here.
 */
export default function AdminBrands() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // Manual brand management — drawer + audit history state.
  const [manualDrawerOpen, setManualDrawerOpen] = useState(false);
  const [editingManualBrand, setEditingManualBrand] = useState(null);
  const [auditDrawerOpen, setAuditDrawerOpen] = useState(false);
  const [auditBrand, setAuditBrand] = useState(null);

  // Inline editors (commission % + geo).
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [geoEditingId, setGeoEditingId] = useState(null);
  const [geoEditValue, setGeoEditValue] = useState("");
  const [geoSaving, setGeoSaving] = useState(false);

  // Drawers / import dialogs.
  const [contractOpen, setContractOpen] = useState(false);
  const [contractBrand, setContractBrand] = useState(null);
  const [driftSet, setDriftSet] = useState(new Set());
  const [partnerizeOpen, setPartnerizeOpen] = useState(false);
  const [skimlinksOpen, setSkimlinksOpen] = useState(false);
  const [mrgeImportOpen, setMrgeImportOpen] = useState(false);

  // Catalog filter + server-side pagination.
  const [networkFilter, setNetworkFilter] = useState("all");
  const [hasTraffic30d, setHasTraffic30d] = useState(false);  // "active brands" toggle
  const [hideInactive, setHideInactive] = useState(true);  // hide upstream-removed brands
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  // Traffic rules drawer (admin sets daily click cap + allowed traffic types).
  const [trafficRulesBrand, setTrafficRulesBrand] = useState(null);
  const [refPolicyBrand, setRefPolicyBrand] = useState(null);
  const [exporting, setExporting] = useState(false);

  // ─── Data fetching ──────────────────────────────────────────────
  const fetchDrift = useCallback(async () => {
    try {
      const { data } = await API.get("/admin/contracts/drift-brands");
      setDriftSet(new Set((data.brands || []).map((b) => b.brand_id)));
    } catch {
      // non-critical
    }
  }, []);

  const fetchBrands = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (networkFilter && networkFilter !== "all") params.network = networkFilter;
      if (search && search.trim()) params.q = search.trim();
      if (hasTraffic30d) params.has_traffic_30d = 1;
      if (hideInactive) params.hide_inactive = 1;
      const { data } = await API.get("/admin/brands", { params });
      // New paginated shape: {items, total, page, page_size, total_pages}.
      // Old shape (array) kept as a safety fallback.
      if (Array.isArray(data)) {
        setBrands(data);
        setTotalCount(data.length);
        setTotalPages(1);
      } else {
        setBrands(data.items || []);
        setTotalCount(data.total || 0);
        setTotalPages(data.total_pages || 1);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [networkFilter, search, page, pageSize, hasTraffic30d, hideInactive]);

  useEffect(() => { fetchBrands(); fetchDrift(); }, [fetchBrands, fetchDrift]);

  // Debounce search input — server-side regex on 25k mrge rows is
  // cheap-ish but firing per keystroke triggers a useless flood.
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Reset to page 1 whenever the network filter or has-traffic filter changes.
  useEffect(() => { setPage(1); }, [networkFilter, hasTraffic30d, hideInactive]);

  // ─── Inline editors ─────────────────────────────────────────────
  const saveCommission = async (campaignId, isFlatLike) => {
    try {
      // Flat-rate (per-lead/per-sale) and tiered brands price off
      // flat_cpa_amount, not base_commission_pct — PATCHing the percent
      // field for these would silently do nothing to actual publisher
      // earnings.
      const body = isFlatLike
        ? { flat_cpa_amount: parseFloat(editValue) }
        : { base_commission_pct: parseFloat(editValue) };
      await API.put(`/admin/brands/${campaignId}`, body);
      toast.success("Commission updated");
      setEditingId(null);
      fetchBrands();
    } catch {
      toast.error("Failed to update");
    }
  };

  // GEO inline save — admin types a comma/space separated list of
  // ISO-2 country codes. Backend computes diff vs upstream + persists
  // admin overrides; we patch the in-memory list so the chip updates
  // immediately without a refetch round-trip.
  const saveGeo = async (campaignId) => {
    const codes = (geoEditValue || "")
      .split(/[,\s]+/)
      .map((c) => c.trim().toUpperCase())
      .filter((c) => /^[A-Z]{2}$/.test(c));
    setGeoSaving(true);
    try {
      const { data } = await API.patch(`/admin/brands/${campaignId}/geo`, { set: codes });
      setBrands((prev) =>
        prev.map((b) =>
          b.campaign_id === campaignId
            ? { ...b, geo_locations: data.geo_locations, geo_admin_overrides: data.geo_admin_overrides }
            : b,
        ),
      );
      toast.success(`GEO updated · ${data.geo_locations.length} country${data.geo_locations.length === 1 ? "" : "ies"}`);
      setGeoEditingId(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update GEO");
    } finally {
      setGeoSaving(false);
    }
  };

  // ─── Row-level actions wired through to drawers ────────────────
  const handleToggleOfferType = async (brand, next) => {
    try {
      await API.patch(`/admin/brands/${brand.campaign_id}/offer-type`, { offer_type: next });
      toast.success(`Set ${brand.name} → ${next.toUpperCase()}`);
      fetchBrands();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const handleDeleteManual = async (brand) => {
    if (!window.confirm(`Delete "${brand.name}"? Existing assignments stay intact — only hidden from lists. Can be restored from audit history.`)) return;
    try {
      await API.delete(`/admin/brands/${brand.campaign_id}/manual`);
      toast.success(`Deleted "${brand.name}"`);
      fetchBrands();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  // ─── Per-network bulk actions (bundled into a hook) ────────────
  const networkActions = useNetworkActions(fetchBrands);

  // Excel export — streams every brand (or just the picked network) into
  // an .xlsx with one sheet per network so the admin can email it out.
  const exportBrandsXlsx = async () => {
    setExporting(true);
    try {
      const params = networkFilter !== "all" ? { network: networkFilter } : {};
      const response = await API.get("/admin/brands/export.xlsx", { params, responseType: "blob" });
      const disp = response.headers["content-disposition"] || "";
      const m = disp.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `redclub_brand_offers_${networkFilter}.xlsx`;
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(
        networkFilter !== "all"
          ? `${networkFilter} brands exported — check your Downloads`
          : "Full catalog exported — check your Downloads",
      );
    } catch (err) {
      // `responseType: "blob"` applies to the ERROR body too, so
      // `err.response.data` is a Blob and `.detail` is always undefined —
      // the 413's "Over the 60,000-brand export limit. Filter by network
      // (?network=…)" guidance never reached the admin, who just saw
      // "Failed to export catalog" with no hint at the way out.
      let detail = "";
      const body = err?.response?.data;
      try {
        detail = typeof body?.text === "function"
          ? JSON.parse(await body.text())?.detail
          : body?.detail;
      } catch { /* not JSON — fall through to the generic message */ }
      toast.error(detail || "Failed to export catalog");
    } finally {
      setExporting(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────
  return (
    <div className="p-8" data-testid="admin-brands-page">
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-slate-400 mb-2">
          Catalog / Brand Network Hub
        </p>
        <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight leading-none">Brands</h1>
        <p className="text-sm text-slate-500 mt-2">
          {totalCount.toLocaleString()} brand{totalCount === 1 ? "" : "s"} {networkFilter !== "all" ? `on ${networkFilter}` : "across all feed partners"}. Pick a network below to filter, sync, or look up brands on demand.
        </p>
      </div>

      <NetworkHubPanel
        selectedNetwork={networkFilter}
        onSelectNetwork={setNetworkFilter}
        onCatalogChanged={fetchBrands}
      />

      <NetworkActionsBar
        networkFilter={networkFilter}
        onOpenPartnerizeImport={() => setPartnerizeOpen(true)}
        onOpenSkimlinksImport={() => setSkimlinksOpen(true)}
        onOpenMrgeImport={() => setMrgeImportOpen(true)}
        onRefreshSkim={networkActions.refreshSkimlinksRates}
        refreshingSkim={networkActions.refreshingSkim}
        onSyncSkimReports={networkActions.syncSkimlinksReports}
        syncingSkimReports={networkActions.syncingSkimReports}
        onSyncSkimPayments={networkActions.syncSkimlinksPayments}
        syncingSkimPayments={networkActions.syncingSkimPayments}
        onMarkSkimNc={networkActions.markSkimlinksPendingNonCommissionable}
        markingSkimNc={networkActions.markingSkimNc}
        onBackfillMrge={networkActions.backfillMrgeRatesFromSummary}
        backfillingMrge={networkActions.backfillingMrge}
        onDeepRefreshMrge={networkActions.startMrgeDeepRateRefresh}
        mrgeRateRun={networkActions.mrgeRateRun}
        onImportCj={networkActions.importCjBrands}
        onSyncShopnomix={networkActions.syncShopnomixCatalog}
        syncingShopnomix={networkActions.syncingShopnomix}
        onSyncShopnomixReports={networkActions.syncShopnomixReports}
        syncingShopnomixReports={networkActions.syncingShopnomixReports}
        onSyncPronomix={networkActions.syncPronomixCatalog}
        syncingPronomix={networkActions.syncingPronomix}
        onAddManual={() => { setEditingManualBrand(null); setManualDrawerOpen(true); }}
      />

      <BrandsFilterBar
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        networkFilter={networkFilter}
        onNetworkFilterChange={setNetworkFilter}
        hasTraffic30d={hasTraffic30d}
        onHasTraffic30dChange={setHasTraffic30d}
        hideInactive={hideInactive}
        onHideInactiveChange={setHideInactive}
        totalCount={totalCount}
        exporting={exporting}
        onExport={exportBrandsXlsx}
      />

      <EmptySearchPrompt
        searchInput={searchInput}
        networkFilter={networkFilter}
        brandsLength={brands.length}
        loading={loading}
        onLookupComplete={fetchBrands}
      />

      <BrandsTable
        brands={brands}
        loading={loading}
        hasDrift={(cid) => driftSet.has(cid)}
        editingId={editingId}
        editValue={editValue}
        setEditingId={setEditingId}
        setEditValue={setEditValue}
        onSaveCommission={saveCommission}
        geoEditingId={geoEditingId}
        geoEditValue={geoEditValue}
        setGeoEditingId={setGeoEditingId}
        setGeoEditValue={setGeoEditValue}
        geoSaving={geoSaving}
        onSaveGeo={saveGeo}
        onToggleOfferType={handleToggleOfferType}
        onTrafficRules={(b) => setTrafficRulesBrand(b)}
        onRefPolicy={(b) => setRefPolicyBrand(b)}
        onContract={(brand) => { setContractBrand(brand); setContractOpen(true); }}
        onEditManual={(brand) => { setEditingManualBrand(brand); setManualDrawerOpen(true); }}
        onAuditManual={(brand) => { setAuditBrand(brand); setAuditDrawerOpen(true); }}
        onDeleteManual={handleDeleteManual}
      />

      {!loading && (
        <BrandsPagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          networkFilter={networkFilter}
          onPageChange={setPage}
        />
      )}

      <ContractDrawer
        open={contractOpen}
        onOpenChange={setContractOpen}
        brand={contractBrand}
        onSaved={() => { fetchDrift(); }}
      />
      <PartnerizeImportDialog
        open={partnerizeOpen}
        onOpenChange={setPartnerizeOpen}
        onImported={fetchBrands}
      />
      <SkimlinksImportDialog
        open={skimlinksOpen}
        onOpenChange={setSkimlinksOpen}
        onImported={fetchBrands}
      />
      <MrgeImportDialog
        open={mrgeImportOpen}
        onClose={() => setMrgeImportOpen(false)}
        onImported={fetchBrands}
      />

      {/* Manual brand management — create / edit drawer + audit history. */}
      <ManualBrandDrawer
        open={manualDrawerOpen}
        onClose={() => { setManualDrawerOpen(false); setEditingManualBrand(null); }}
        mode={editingManualBrand ? "edit" : "create"}
        brand={editingManualBrand}
        onSaved={fetchBrands}
      />
      <BrandAuditDrawer
        open={auditDrawerOpen}
        onClose={() => { setAuditDrawerOpen(false); setAuditBrand(null); }}
        brand={auditBrand}
        onRolledBack={fetchBrands}
      />

      <TrafficRulesDrawer
        open={!!trafficRulesBrand}
        onClose={() => setTrafficRulesBrand(null)}
        scope="brand"
        brand={trafficRulesBrand}
        onSaved={fetchBrands}
      />

      <BrandRefPolicyDrawer
        open={!!refPolicyBrand}
        onOpenChange={(v) => { if (!v) setRefPolicyBrand(null); }}
        brand={refPolicyBrand}
        onSaved={fetchBrands}
      />
    </div>
  );
}
